import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import * as React from "react";

import { REASON_LABEL } from "@/components/chat/answer-rating";
import { AccuracySection } from "@/components/evaluations/accuracy-section";
import { HBars, StatusChip, TrendChart, asPct } from "@/components/evaluations/charts";
import { KpiRow, PeriodSelect, RatedAnswers, dayLabel } from "@/components/evaluations/parts";
import { PageHeader } from "@/components/layout-bits";
import { Link } from "@/components/ui/link";
import { Card, Skeleton } from "@/components/ui/primitives";
import { ErrorState } from "@/components/ui/states";
import { getAgentEvaluation } from "@/lib/api/chat";
import { useParams } from "@/lib/navigation";

const SERIES = { name: "Approval", color: "var(--series-1)" };

/** One agent's evaluation: approval over time, by version, by reason, and its rated answers. */
export default function EvaluationAgentPage() {
  const slug = String(useParams().slug);
  const [days, setDays] = React.useState(30);
  const q = useQuery({
    queryKey: ["my", "evaluations", slug, days],
    queryFn: () => getAgentEvaluation(slug, days),
  });
  const a = q.data;

  return (
    <div>
      <Link
        href="/evaluations"
        className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Evaluations
      </Link>
      <PageHeader
        title={a ? a.name : slug}
        description={
          a
            ? `${a.live_version ? `Live version v${a.live_version}` : "No live version"} · ${a.kpis.up + a.kpis.down} rating${a.kpis.up + a.kpis.down === 1 ? "" : "s"} in the last ${days} days`
            : "Agent evaluation"
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {a ? <StatusChip status={a.status} /> : null}
            <PeriodSelect value={days} onChange={setDays} />
          </div>
        }
      />

      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : q.isLoading || !a ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <div className="grid gap-6">
          <KpiRow
            kpis={a.kpis}
            days={days}
            third={{
              label: "Top issue",
              value: a.top_reason ? REASON_LABEL[a.top_reason] : "None",
              sub: "most common thumbs-down reason",
            }}
          />

          <div className="grid gap-3 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
            <Card className="grid gap-2 p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold">Approval rate over time</h3>
                <span className="text-xs text-muted-foreground">
                  % of ratings that are 👍, per day
                </span>
              </div>
              <TrendChart
                labels={a.trend.map((p) => dayLabel(p.date))}
                series={[{ ...SERIES, data: a.trend.map((p) => p.approval) }]}
              />
            </Card>
            <Card className="grid content-start gap-4 p-4">
              <div className="grid gap-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-sm font-semibold">Approval by version</h3>
                  <span className="text-xs text-muted-foreground">compare before promoting</span>
                </div>
                {a.versions.length ? (
                  <HBars
                    max={1}
                    format={(v) => asPct(v)}
                    rows={a.versions.map((v) => ({
                      label: `v${v.version}${v.live ? " · live" : ""}`,
                      value: v.approval_rate,
                      hint: `v${v.version}: 👍 ${v.up} · 👎 ${v.down}`,
                    }))}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">No versions rated in this period.</p>
                )}
              </div>
              <div className="grid gap-3">
                <h3 className="text-sm font-semibold">Why answers were rated down</h3>
                {a.kpis.down ? (
                  <HBars
                    rows={a.reasons.map((r) => ({
                      label: REASON_LABEL[r.category],
                      value: r.count,
                      hint: `${REASON_LABEL[r.category]}: ${r.count}`,
                    }))}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">No thumbs-downs in this period.</p>
                )}
              </div>
            </Card>
          </div>

          <AccuracySection evaluation={a} days={days} />

          <RatedAnswers agent={slug} days={days} versions={a.versions.map((v) => v.version)} />
        </div>
      )}
    </div>
  );
}
